import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearActivityDetails } from "./activitySlice";
import {
  createActivity,
  getAllActiveActivities,
  getAllActivities,
  getActivityByID,
  updateActivity,
} from "./service/activity.services";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { closed } from "../../components/drawer/drawerSlice";
import IconComponent from "../../components/common/IconComponent";

const CreateActivity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const ActivityDetail = useAppSelector(
    (state) => state.activity.ActivityDetail,
  );
  const ActivityID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.activity.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  const [formKey, setFormKey] = React.useState<number>(1);

  useEffect(() => {
    return () => {
      dispatch(clearActivityDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ActivityDetail]);

  useEffect(() => {
    if (ActivityID) {
      dispatch(getActivityByID(ActivityID));
    }
  }, [ActivityID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!ActivityID) {
        const insertPayload = {
          ActivityName: values.ActivityName,
          ActivityCode: values.ActivityCode,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(createActivity(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearActivityDetails());
          if (isIndirect) {
            dispatch(getAllActiveActivities());
          } else {
            dispatch(getAllActivities());
          }
        }
      } else {
        const editPayload = {
          ID: ActivityID,
          ActivityName: values.ActivityName,
          ActivityCode: values.ActivityCode,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(updateActivity(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearActivityDetails());
          dispatch(getAllActivities());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={ActivityDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem className="master-drawer-header">
                <Typography.h4 className="m-0">
                  {ActivityID ? "Update Activity" : "Add Activity"}
                </Typography.h4>
                <Button
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <IconComponent icon={RxCross1} />
                </Button>
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="ActivityName"
                  label="Activity Name"
                  placeholder="i.e. Para Sailing"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  astrike={true}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  textAlign: "start",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={ActivityID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateActivity;
